import React, { useState } from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    Image,
    SimpleGrid,
    Button,
    Icon,
    useDisclosure,
    Flex,
    VStack
} from "@chakra-ui/react";
import { useNavigate } from 'react-router-dom';
import { FaBalanceScale, FaGavel, FaHandshake, FaUserTie, FaArrowRight } from "react-icons/fa";
import { GiScales } from "react-icons/gi";
import ArenImg from "../../assets/images/Aren.png";
import AshotImg from "../../assets/images/Ashot.png";
import officePhoto from "../../assets/images/office.jpeg";
import ExperienceModal from "../../components/modals/ExperienceModal";
import SEO from "../../components/SEO";
import { useLanguage } from "../../context/LanguageContext";

const AboutPage = () => {
    const { t } = useLanguage();
    const navigate = useNavigate();

    const stats = [
        { value: "5+", label: t("about.stats.0.label") },
        { value: "200+", label: t("about.stats.1.label") },
        { value: "94%", label: t("about.stats.2.label") }
    ];

    const values = [
        {
            icon: FaBalanceScale,
            title: t("about.values.0.title"),
            description: t("about.values.0.description")
        },
        {
            icon: FaGavel,
            title: t("about.values.1.title"),
            description: t("about.values.1.description")
        },
        {
            icon: FaHandshake,
            title: t("about.values.2.title"),
            description: t("about.values.2.description")
        },
        {
            icon: FaUserTie,
            title: t("about.values.3.title"),
            description: t("about.values.3.description")
        }
    ];

    const teamMembers = [
        {
            name: t("about.team.0.name"),
            role: t("about.team.0.role"),
            specialization: t("about.team.0.specialization"),
            experience: t("about.team.0.experience"),
            experienceLabel: t("about.team.0.experienceLabel"),
            photo: ArenImg,
            memberKey: "aren"
        },
        {
            name: t("about.team.1.name"),
            role: t("about.team.1.role"),
            specialization: t("about.team.1.specialization"),
            experience: t("about.team.1.experience"),
            experienceLabel: t("about.team.1.experienceLabel"),
            photo: AshotImg,
            memberKey: "ashot"
        }
    ];

    const [selectedMemberIndex, setSelectedMemberIndex] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const selectedMember = teamMembers[selectedMemberIndex];

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "url": "https://legaltitan.am/about",
        "logo": "https://legaltitan.am/assets/images/legalLogo.jpg",
        "name": "LegalTitan",
        "sameAs": [
            "https://www.facebook.com/profile.php?id=61557661694303"
        ]
    };

    return (
        <Box bg="#f8fafc" minH="100vh">
            <SEO
                title="Մեր Մասին | LegalTitan"
                description="LegalTitan.am – հուսալի իրավաբանական ծառայություններ Հայաստանում։ Փորձառու իրավաբաններ, պաշտպանություն և խորհրդատվություն տարբեր իրավական ոլորտներում։"
                keywords="իրավաբան, իրավաբանական ծառայություններ, իրավաբանական աջակցություն, իրավական խորհրդատվություն, կորպորատիվ իրավունք, քաղաքացիական իրավունք"
                url="https://legaltitan.am/about"
                image="https://legaltitan.am/assets/images/legalLogo.jpg"
                fbAppId="61557661694303"
                structuredData={structuredData}
            />

            {/* Executive Hero Banner */}
            <Box
                bg="#070d1e"
                color="white"
                pt={{ base: 14, md: 20 }}
                pb={{ base: 16, md: 24 }}
                position="relative"
                overflow="hidden"
            >
                <Box
                    position="absolute"
                    top="-30%"
                    left="20%"
                    w="500px"
                    h="500px"
                    bg="radial-gradient(circle, rgba(29, 78, 216, 0.2) 0%, transparent 70%)"
                    pointerEvents="none"
                />

                <Container maxW="container.xl" px={{ base: 4, md: 8 }} position="relative" zIndex={1} textAlign="center">
                    <Heading
                        as="h1"
                        fontSize={{ base: "32px", sm: "42px", md: "52px" }}
                        fontWeight="900"
                        letterSpacing="-0.03em"
                        lineHeight="1.2"
                        mb={5}
                    >
                        {t("about.pageTitle")}
                    </Heading>

                    <Text
                        fontSize={{ base: "md", md: "lg", lg: "xl" }}
                        color="gray.300"
                        maxW="780px"
                        mx="auto"
                        lineHeight="1.8"
                    >
                        {t("about.pageSubtitle")}
                    </Text>
                </Container>
            </Box>

            {/* Floating Metric Counters */}
            <Box mt={{ base: "-30px", md: "-45px" }} position="relative" zIndex={3}>
                <Container maxW="container.lg" px={{ base: 4, md: 8 }}>
                    <SimpleGrid
                        columns={{ base: 1, sm: 3 }}
                        spacing={{ base: 4, md: 6 }}
                        bg="white"
                        p={{ base: 6, md: 8 }}
                        borderRadius="2xl"
                        boxShadow="0 20px 40px -15px rgba(11, 19, 43, 0.12)"
                        border="1px solid"
                        borderColor="gray.100"
                    >
                        {stats.map((stat, index) => (
                            <Box key={index} textAlign="center" py={{ base: 2, md: 0 }}>
                                <Heading
                                    as="h3"
                                    fontSize={{ base: "36px", md: "46px" }}
                                    fontWeight="900"
                                    color="#0b132b"
                                    lineHeight="1"
                                    mb={2}
                                >
                                    {stat.value}
                                </Heading>
                                <Text fontWeight="700" color="gray.700" fontSize="sm">
                                    {stat.label}
                                </Text>
                            </Box>
                        ))}
                    </SimpleGrid>
                </Container>
            </Box>

            {/* Founders Leadership Showcase */}
            <Box py={{ base: 14, md: 24 }}>
                <Container maxW="container.xl" px={{ base: 4, md: 8 }}>
                    <Box textAlign="center" maxW="700px" mx="auto" mb={{ base: 10, md: 16 }}>
                        <Heading
                            as="h2"
                            fontSize={{ base: "26px", sm: "32px", md: "40px" }}
                            fontWeight="800"
                            color="gray.900"
                            letterSpacing="-0.02em"
                        >
                            {t("about.foundersTitle")}
                        </Heading>
                    </Box>

                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 8, md: 10 }} maxW="950px" mx="auto">
                        {teamMembers.map((member, index) => (
                            <Box
                                key={index}
                                bg="white"
                                borderRadius="2xl"
                                border="1px solid"
                                borderColor="gray.100"
                                overflow="hidden"
                                boxShadow="0 10px 30px -5px rgba(15, 23, 42, 0.06)"
                                transition="all 0.3s cubic-bezier(0.16, 1, 0.3, 1)"
                                _hover={{
                                    transform: "translateY(-6px)",
                                    boxShadow: "0 25px 45px -10px rgba(15, 23, 42, 0.15)",
                                    borderColor: "#dfba73",
                                }}
                            >
                                <Box position="relative" h={{ base: "280px", sm: "340px" }} overflow="hidden" bg="gray.100">
                                    <Image
                                        src={member.photo}
                                        alt={member.name}
                                        w="100%"
                                        h="100%"
                                        objectFit="cover"
                                        objectPosition="top"
                                    />
                                    <Box
                                        position="absolute"
                                        inset={0}
                                        bg="linear-gradient(to top, rgba(7, 13, 30, 0.8) 0%, transparent 60%)"
                                    />
                                    <Box position="absolute" bottom={4} left={5} right={5}>
                                        <Heading as="h3" color="white" fontSize={{ base: "xl", md: "2xl" }} fontWeight="800">
                                            {member.name}
                                        </Heading>
                                        <Text color="#dfba73" fontWeight="700" fontSize="sm" mt={0.5}>
                                            {member.role}
                                        </Text>
                                    </Box>
                                </Box>

                                <Box p={6}>
                                    <VStack align="flex-start" spacing={3} mb={5}>
                                        <Box>
                                            <Text fontSize="xs" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.05em">
                                                {member.experienceLabel}
                                            </Text>
                                            <Text fontSize="sm" fontWeight="600" color="gray.800">
                                                {member.experience}
                                            </Text>
                                        </Box>
                                        <Box>
                                            <Text fontSize="xs" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.05em">
                                                Մասնագիտացում
                                            </Text>
                                            <Text fontSize="xs" color="gray.600" lineHeight="1.6">
                                                {member.specialization}
                                            </Text>
                                        </Box>
                                    </VStack>

                                    <Button
                                        w="100%"
                                        variant="outline"
                                        borderColor="#0b132b"
                                        color="#0b132b"
                                        size="md"
                                        borderRadius="xl"
                                        fontWeight="700"
                                        _hover={{ bg: "#0b132b", color: "#dfba73" }}
                                        onClick={() => {
                                            setSelectedMemberIndex(index);
                                            onOpen();
                                        }}
                                        rightIcon={<FaArrowRight />}
                                    >
                                        {t("about.btnViewDetails")}
                                    </Button>
                                </Box>
                            </Box>
                        ))}
                    </SimpleGrid>

                    <ExperienceModal isOpen={isOpen} onClose={onClose} member={selectedMember} />
                </Container>
            </Box>

            {/* Our Story & History */}
            <Box py={{ base: 14, md: 20 }} bg="white" borderTop="1px solid" borderColor="gray.100">
                <Container maxW="container.xl" px={{ base: 4, md: 8 }}>
                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 10, lg: 16 }} align="center">
                        <Box>
                            <Heading
                                as="h2"
                                fontSize={{ base: "26px", sm: "32px", md: "38px" }}
                                fontWeight="800"
                                color="gray.900"
                                letterSpacing="-0.02em"
                                mb={6}
                            >
                                {t("about.historyTitle")}
                            </Heading>

                            <Text fontSize={{ base: "md", md: "lg" }} color="gray.700" lineHeight="1.8" mb={4}>
                                {t("about.historyP1")}
                            </Text>

                            <Text fontSize={{ base: "md", md: "lg" }} color="gray.700" lineHeight="1.8" mb={4}>
                                {t("about.historyP2")}
                            </Text>

                            <Text fontSize={{ base: "md", md: "lg" }} color="gray.700" lineHeight="1.8">
                                {t("about.historyP3")}
                            </Text>
                        </Box>

                        <Box position="relative">
                            <Box
                                borderRadius="2xl"
                                overflow="hidden"
                                boxShadow="0 20px 40px -10px rgba(11, 19, 43, 0.2)"
                            >
                                <Image
                                    src={officePhoto}
                                    alt="Legal Titan Office"
                                    w="100%"
                                    h={{ base: "300px", md: "420px" }}
                                    objectFit="cover"
                                />
                            </Box>
                        </Box>
                    </SimpleGrid>
                </Container>
            </Box>

            {/* Values Grid */}
            <Box py={{ base: 14, md: 24 }} bg="#f8fafc">
                <Container maxW="container.xl" px={{ base: 4, md: 8 }}>
                    <Box textAlign="center" maxW="700px" mx="auto" mb={{ base: 10, md: 16 }}>
                        <Heading
                            as="h2"
                            fontSize={{ base: "26px", sm: "32px", md: "40px" }}
                            fontWeight="800"
                            color="gray.900"
                            letterSpacing="-0.02em"
                        >
                            {t("about.valuesTitle")}
                        </Heading>
                    </Box>

                    <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={6}>
                        {values.map((val, idx) => (
                            <Box
                                key={idx}
                                bg="white"
                                p={7}
                                borderRadius="2xl"
                                border="1px solid"
                                borderColor="gray.100"
                                boxShadow="0 10px 25px -5px rgba(15, 23, 42, 0.04)"
                                transition="all 0.3s ease"
                                _hover={{
                                    transform: "translateY(-6px)",
                                    boxShadow: "0 20px 35px -10px rgba(15, 23, 42, 0.1)",
                                    borderColor: "#dfba73",
                                }}
                            >
                                <Flex
                                    w={12}
                                    h={12}
                                    borderRadius="xl"
                                    bg="brand.50"
                                    color="brand.600"
                                    align="center"
                                    justify="center"
                                    mb={5}
                                >
                                    <Icon as={val.icon} fontSize="22px" />
                                </Flex>
                                <Heading as="h3" fontSize="md" fontWeight="800" color="gray.900" mb={3}>
                                    {val.title}
                                </Heading>
                                <Text fontSize="sm" color="gray.600" lineHeight="1.7">
                                    {val.description}
                                </Text>
                            </Box>
                        ))}
                    </SimpleGrid>
                </Container>
            </Box>

            {/* Bottom CTA */}
            <Box py={{ base: 12, md: 18 }} bg="#070d1e" color="white" textAlign="center">
                <Container maxW="container.md" px={{ base: 4, md: 8 }}>
                    <Icon as={GiScales} fontSize="40px" color="#dfba73" mb={4} />
                    <Heading as="h2" fontSize={{ base: "22px", md: "32px" }} fontWeight="800" mb={4}>
                        {t("about.ctaTitle")}
                    </Heading>
                    <Text color="gray.300" fontSize={{ base: "sm", md: "md" }} mb={6}>
                        {t("about.ctaText")}
                    </Text>
                    <Button
                        size="lg"
                        bg="linear-gradient(135deg, #dfba73 0%, #c5a880 100%)"
                        color="#0b132b"
                        fontWeight="700"
                        borderRadius="xl"
                        px={8}
                        _hover={{ transform: "translateY(-2px)" }}
                        onClick={() => navigate('/contact')}
                    >
                        {t("about.btnContactUs")}
                    </Button>
                </Container>
            </Box>
        </Box>
    );
};

export default AboutPage;
